import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeftRight,
  BedDouble,
  Car,
  Package,
  Plane,
  Search,
  Ship,
} from 'lucide-react';
import { POPULAR_CITY_PLACES, POPULAR_FLIGHT_PLACES } from '../constants/popularPlaces';
import { formatPlace } from '../utils/placeFormat';
import { formatDateParts } from '../utils/dateFormat';
import DatePickerModal from './DatePickerModal';
import TravellerPickerModal from './TravellerPickerModal';
import CitySelector from './CitySelector';

const TABS = [
  { id: 'flights', label: 'Flights', heroLabel: 'Flights', Icon: Plane },
  { id: 'hotels', label: 'Hotels', heroLabel: 'Hotel', Icon: BedDouble },
  { id: 'cars', label: 'Cars', heroLabel: 'Cars', Icon: Car },
  { id: 'packages', label: 'Packages', heroLabel: 'Packages', Icon: Package },
  { id: 'cruises', label: 'Cruises', heroLabel: 'Cruise', Icon: Ship },
];

const today = new Date();
const fmt = (d) => d.toISOString().slice(0, 10);
const nextWeek = new Date(today);
nextWeek.setDate(today.getDate() + 7);
const twoWeeks = new Date(today);
twoWeeks.setDate(today.getDate() + 14);

function SwField({ label, children, span, hidden, className = '' }) {
  const spanClass = span ? `sw-cell--span-${span}` : '';
  const hiddenClass = hidden ? 'sw-cell--hidden' : '';
  return (
    <label className={`sw-cell ${spanClass} ${hiddenClass} ${className}`.trim()}>
      <span className="sw-cell__label">{label}</span>
      <div className="sw-cell__value">{children}</div>
    </label>
  );
}

function SwDate({ label, value, inactive = false, onClick }) {
  const parts = formatDateParts(inactive ? '' : value);
  return (
    <button
      type="button"
      className={`sw-cell sw-cell--date${inactive ? ' sw-cell--inactive' : ''}`}
      onClick={onClick}
      disabled={inactive}
      aria-label={inactive ? label : `${label}: ${value}`}
    >
      <span className="sw-cell__label">{label}</span>
      <div className="sw-cell__value sw-cell__value--date">
        <span className="sw-date__day">{inactive ? '—' : parts.day}</span>
        <span className="sw-date__meta">
          <span>{inactive ? '—' : parts.month}</span>
          <span>{inactive ? '' : parts.weekday}</span>
        </span>
      </div>
    </button>
  );
}

function SwMeta({ label, display, onClick, ariaLabel }) {
  return (
    <button type="button" className="sw-cell sw-cell--meta" onClick={onClick} aria-label={ariaLabel}>
      <span className="sw-cell__label">{label}</span>
      <div className="sw-cell__value sw-cell__value--meta">{display}</div>
    </button>
  );
}

const CABIN_LABELS = {
  economy: 'Economy',
  premium: 'Premium',
  business: 'Business',
  first: 'First',
};

export default function SearchWidget({ defaultTab = 'flights', variant = 'default' }) {
  const isHero = variant === 'hero';
  const navigate = useNavigate();
  const [tab, setTab] = useState(defaultTab);
  const [flightTrip, setFlightTrip] = useState('roundtrip');
  const [dateModal, setDateModal] = useState(null);
  const [pickerModal, setPickerModal] = useState(null);

  const [flight, setFlight] = useState({
    from: formatPlace(POPULAR_FLIGHT_PLACES[0]),
    to: formatPlace(POPULAR_FLIGHT_PLACES[6]),
    depart: fmt(today),
    return: fmt(nextWeek),
    adults: 1,
    children: 0,
    cabin: 'economy',
  });
  const [hotel, setHotel] = useState({
    destination: formatPlace(POPULAR_CITY_PLACES[0]),
    checkIn: fmt(nextWeek),
    checkOut: fmt(twoWeeks),
    adults: 2,
    children: 0,
    rooms: 1,
  });
  const [car, setCar] = useState({
    pickup: formatPlace(POPULAR_FLIGHT_PLACES[5]),
    dropoff: formatPlace(POPULAR_FLIGHT_PLACES[5]),
    pickupDate: fmt(nextWeek),
    dropoffDate: fmt(twoWeeks),
    carType: 'any',
  });
  const [pkg, setPkg] = useState({
    destination: formatPlace(POPULAR_CITY_PLACES.find((p) => p.code === 'NYC') || POPULAR_CITY_PLACES[2]),
    depart: fmt(nextWeek),
    return: fmt(twoWeeks),
    adults: 2,
    children: 0,
  });
  const [cruise, setCruise] = useState({
    port: formatPlace(POPULAR_FLIGHT_PLACES[6]),
    destination: 'Caribbean',
    depart: fmt(twoWeeks),
    adults: 2,
    children: 0,
  });

  const openDate = (config) => setDateModal(config);
  const closeDate = () => setDateModal(null);
  const openPicker = (config) => setPickerModal(config);
  const closePicker = () => setPickerModal(null);

  const buildParams = (obj) =>
    Object.entries(obj)
      .filter(([, v]) => v != null && v !== '')
      .map(([k, v]) => `${k}=${encodeURIComponent(v)}`)
      .join('&');

  const swapFlightEnds = () => {
    setFlight((f) => ({ ...f, from: f.to, to: f.from }));
  };

  const flightPax = flight.adults + flight.children;
  const hotelGuests = hotel.adults + hotel.children;
  const pkgPax = pkg.adults + pkg.children;
  const cruisePax = cruise.adults + cruise.children;

  const handleSearch = (e) => {
    e.preventDefault();
    if (tab === 'flights') {
      const params = {
        ...flight,
        trip: flightTrip,
        passengers: String(flightPax),
      };
      if (flightTrip === 'oneway') delete params.return;
      navigate(`/flight/search/flights?${buildParams(params)}`);
    } else if (tab === 'hotels') {
      navigate(
        `/flight/search/hotels?${buildParams({
          ...hotel,
          guests: String(hotelGuests),
        })}`
      );
    } else if (tab === 'cars') {
      navigate(`/flight/search/cars?${buildParams(car)}`);
    } else if (tab === 'packages') {
      navigate(
        `/flight/search/packages?${buildParams({
          ...pkg,
          travelers: String(pkgPax),
        })}`
      );
    } else {
      navigate(
        `/flight/search/cruises?${buildParams({
          ...cruise,
          guests: String(cruisePax),
        })}`
      );
    }
  };

  const renderToolbar = () => {
    if (tab === 'flights') {
      return (
        <div className="sw-toolbar">
          <div className="sw-toolbar__trip">
            {['roundtrip', 'oneway'].map((t) => (
              <label key={t} className="sw-radio">
                <input
                  type="radio"
                  name="trip"
                  checked={flightTrip === t}
                  onChange={() => setFlightTrip(t)}
                />
                {t === 'roundtrip' ? 'Round Trip' : 'One Way'}
              </label>
            ))}
            {!isHero && (
              <label className="sw-radio">
                <input
                  type="radio"
                  name="trip"
                  checked={flightTrip === 'multicity'}
                  onChange={() => setFlightTrip('multicity')}
                />
                Multi-city
              </label>
            )}
          </div>
          <select
            className="sw-toolbar__select"
            value={flight.cabin}
            onChange={(e) => setFlight({ ...flight, cabin: e.target.value })}
            aria-label="Cabin class"
          >
            <option value="economy">Economy</option>
            <option value="premium">Premium Economy</option>
            <option value="business">Business</option>
            <option value="first">First</option>
          </select>
        </div>
      );
    }
    return <div className="sw-toolbar sw-toolbar--spacer" aria-hidden="true" />;
  };

  const renderGrid = () => {
    if (tab === 'flights') {
      return (
        <>
          <SwField label="Flying From">
            <CitySelector
              value={flight.from}
              onChange={(v) => setFlight({ ...flight, from: v })}
              placeholder="City or airport"
              required
              popularPlaces={POPULAR_FLIGHT_PLACES}
              types={['city', 'airport']}
            />
          </SwField>

          <button type="button" className="sw-swap" onClick={swapFlightEnds} aria-label="Swap origin and destination">
            <ArrowLeftRight size={18} />
          </button>

          <SwField label="Flying To">
            <CitySelector
              value={flight.to}
              onChange={(v) => setFlight({ ...flight, to: v })}
              placeholder="City or airport"
              required
              popularPlaces={POPULAR_FLIGHT_PLACES}
              types={['city', 'airport']}
            />
          </SwField>

          <SwDate
            label="Departure"
            value={flight.depart}
            onClick={() =>
              openDate({
                title: 'Departure date',
                value: flight.depart,
                min: fmt(today),
                onSelect: (d) => setFlight((f) => ({ ...f, depart: d, return: f.return < d ? d : f.return })),
              })
            }
          />

          <SwDate
            label="Return"
            value={flight.return}
            inactive={flightTrip === 'oneway'}
            onClick={() =>
              openDate({
                title: 'Return date',
                value: flight.return,
                min: flight.depart,
                onSelect: (d) => setFlight((f) => ({ ...f, return: d })),
              })
            }
          />

          <SwMeta
            label="Travellers"
            ariaLabel="Choose travellers"
            onClick={() => openPicker({ type: 'flight' })}
            display={
              <>
                <span className="sw-meta__primary">{String(flightPax).padStart(2, '0')}</span>
                <span className="sw-meta__sub">
                  {flight.adults} Adult{flight.adults !== 1 ? 's' : ''}
                  {flight.children > 0 ? `, ${flight.children} Child${flight.children !== 1 ? 'ren' : ''}` : ''}
                  {' · '}
                  {CABIN_LABELS[flight.cabin]}
                </span>
              </>
            }
          />
        </>
      );
    }

    if (tab === 'hotels') {
      return (
        <>
          <SwField label="Destination" span={3}>
            <CitySelector
              value={hotel.destination}
              onChange={(v) => setHotel({ ...hotel, destination: v })}
              placeholder="Where are you going?"
              required
              popularPlaces={POPULAR_CITY_PLACES}
              types={['city']}
            />
          </SwField>
          <span className="sw-swap sw-swap--hidden" aria-hidden="true" />
          <SwDate
            label="Check-in"
            value={hotel.checkIn}
            onClick={() =>
              openDate({
                title: 'Check-in date',
                value: hotel.checkIn,
                min: fmt(today),
                onSelect: (d) =>
                  setHotel((h) => ({
                    ...h,
                    checkIn: d,
                    checkOut: h.checkOut < d ? d : h.checkOut,
                  })),
              })
            }
          />
          <SwDate
            label="Check-out"
            value={hotel.checkOut}
            onClick={() =>
              openDate({
                title: 'Check-out date',
                value: hotel.checkOut,
                min: hotel.checkIn,
                onSelect: (d) => setHotel((h) => ({ ...h, checkOut: d })),
              })
            }
          />
          <SwMeta
            label="Guests"
            ariaLabel="Choose guests and rooms"
            onClick={() => openPicker({ type: 'hotel' })}
            display={
              <>
                <span className="sw-meta__primary">{String(hotelGuests).padStart(2, '0')}</span>
                <span className="sw-meta__sub">
                  {hotel.adults} Adult{hotel.adults !== 1 ? 's' : ''}
                  {hotel.children > 0 ? `, ${hotel.children} Child${hotel.children !== 1 ? 'ren' : ''}` : ''}
                  {' · '}
                  {hotel.rooms} Room{hotel.rooms !== 1 ? 's' : ''}
                </span>
              </>
            }
          />
        </>
      );
    }

    if (tab === 'cars') {
      return (
        <>
          <SwField label="Pick-up">
            <CitySelector
              value={car.pickup}
              onChange={(v) => setCar({ ...car, pickup: v })}
              placeholder="City or airport"
              required
              popularPlaces={POPULAR_FLIGHT_PLACES}
              types={['city', 'airport']}
            />
          </SwField>
          <span className="sw-swap sw-swap--hidden" aria-hidden="true" />
          <SwField label="Drop-off">
            <CitySelector
              value={car.dropoff}
              onChange={(v) => setCar({ ...car, dropoff: v })}
              placeholder="City or airport"
              required
              popularPlaces={POPULAR_FLIGHT_PLACES}
              types={['city', 'airport']}
            />
          </SwField>
          <SwDate
            label="Pick-up"
            value={car.pickupDate}
            onClick={() =>
              openDate({
                title: 'Pick-up date',
                value: car.pickupDate,
                min: fmt(today),
                onSelect: (d) =>
                  setCar((c) => ({
                    ...c,
                    pickupDate: d,
                    dropoffDate: c.dropoffDate < d ? d : c.dropoffDate,
                  })),
              })
            }
          />
          <SwDate
            label="Drop-off"
            value={car.dropoffDate}
            onClick={() =>
              openDate({
                title: 'Drop-off date',
                value: car.dropoffDate,
                min: car.pickupDate,
                onSelect: (d) => setCar((c) => ({ ...c, dropoffDate: d })),
              })
            }
          />
          <SwMeta
            label="Car type"
            ariaLabel="Choose car type"
            onClick={() => openPicker({ type: 'car' })}
            display={
              <span className="sw-meta__primary sw-meta__primary--text">
                {car.carType === 'any' ? 'Any' : car.carType}
              </span>
            }
          />
        </>
      );
    }

    if (tab === 'packages') {
      return (
        <>
          <SwField label="Destination" span={3}>
            <CitySelector
              value={pkg.destination}
              onChange={(v) => setPkg({ ...pkg, destination: v })}
              placeholder="Package destination"
              required
              popularPlaces={POPULAR_CITY_PLACES}
              types={['city']}
            />
          </SwField>
          <span className="sw-swap sw-swap--hidden" aria-hidden="true" />
          <SwDate
            label="Depart"
            value={pkg.depart}
            onClick={() =>
              openDate({
                title: 'Departure date',
                value: pkg.depart,
                min: fmt(today),
                onSelect: (d) =>
                  setPkg((p) => ({
                    ...p,
                    depart: d,
                    return: p.return < d ? d : p.return,
                  })),
              })
            }
          />
          <SwDate
            label="Return"
            value={pkg.return}
            onClick={() =>
              openDate({
                title: 'Return date',
                value: pkg.return,
                min: pkg.depart,
                onSelect: (d) => setPkg((p) => ({ ...p, return: d })),
              })
            }
          />
          <SwMeta
            label="Travelers"
            ariaLabel="Choose travelers"
            onClick={() => openPicker({ type: 'package' })}
            display={
              <>
                <span className="sw-meta__primary">{String(pkgPax).padStart(2, '0')}</span>
                <span className="sw-meta__sub">
                  {pkg.adults} Adult{pkg.adults !== 1 ? 's' : ''}
                  {pkg.children > 0 ? `, ${pkg.children} Child${pkg.children !== 1 ? 'ren' : ''}` : ''}
                </span>
              </>
            }
          />
        </>
      );
    }

    return (
      <>
        <SwField label="Departure Port">
          <CitySelector
            value={cruise.port}
            onChange={(v) => setCruise({ ...cruise, port: v })}
            placeholder="Departure port or city"
            required
            popularPlaces={POPULAR_FLIGHT_PLACES.filter((p) =>
              ['MIA', 'LAX', 'NYC', 'SYD', 'BCN'].includes(p.code)
            )}
            types={['city', 'airport']}
          />
        </SwField>
        <span className="sw-swap sw-swap--hidden" aria-hidden="true" />
        <SwField label="Region">
          <select value={cruise.destination} onChange={(e) => setCruise({ ...cruise, destination: e.target.value })}>
            <option>Caribbean</option>
            <option>Mediterranean</option>
            <option>Alaska</option>
            <option>Northern Europe</option>
          </select>
        </SwField>
        <SwDate
          label="Depart"
          value={cruise.depart}
          onClick={() =>
            openDate({
              title: 'Departure date',
              value: cruise.depart,
              min: fmt(today),
              onSelect: (d) => setCruise((c) => ({ ...c, depart: d })),
            })
          }
        />
        <SwDate label="Return" inactive />
        <SwMeta
          label="Guests"
          ariaLabel="Choose guests"
          onClick={() => openPicker({ type: 'cruise' })}
          display={
            <>
              <span className="sw-meta__primary">{String(cruisePax).padStart(2, '0')}</span>
              <span className="sw-meta__sub">
                {cruise.adults} Adult{cruise.adults !== 1 ? 's' : ''}
                {cruise.children > 0 ? `, ${cruise.children} Child${cruise.children !== 1 ? 'ren' : ''}` : ''}
              </span>
            </>
          }
        />
      </>
    );
  };

  const pickerConfig = () => {
    if (!pickerModal) return null;
    if (pickerModal.type === 'flight') {
      return {
        variant: 'flight',
        title: 'Travellers & cabin',
        values: { adults: flight.adults, children: flight.children, cabin: flight.cabin },
        onApply: (v) => setFlight((f) => ({ ...f, ...v })),
      };
    }
    if (pickerModal.type === 'hotel') {
      return {
        variant: 'hotel',
        title: 'Guests & rooms',
        values: { adults: hotel.adults, children: hotel.children, rooms: hotel.rooms },
        onApply: (v) => setHotel((h) => ({ ...h, ...v })),
      };
    }
    if (pickerModal.type === 'car') {
      return {
        variant: 'car',
        title: 'Car type',
        values: { carType: car.carType },
        onApply: (v) => setCar((c) => ({ ...c, ...v })),
      };
    }
    if (pickerModal.type === 'package') {
      return {
        variant: 'guests',
        title: 'Travelers',
        values: { adults: pkg.adults, children: pkg.children },
        onApply: (v) => setPkg((p) => ({ ...p, ...v })),
      };
    }
    return {
      variant: 'guests',
      title: 'Guests',
      values: { adults: cruise.adults, children: cruise.children },
      onApply: (v) => setCruise((c) => ({ ...c, ...v })),
    };
  };

  const picker = pickerConfig();
  const rootClass = `search-widget search-widget--fixed ${isHero ? 'search-widget--hero' : 'search-widget--premium'}`;

  return (
    <div className={rootClass}>
      {!isHero && <p className="search-widget__label">Plan your trip</p>}

      <div className="search-widget__tabs" role="tablist">
        {TABS.map(({ id, label, heroLabel, Icon }) => (
          <button
            key={id}
            type="button"
            role="tab"
            aria-selected={tab === id}
            className={tab === id ? 'active' : ''}
            onClick={() => setTab(id)}
          >
            <Icon size={isHero ? 18 : 16} aria-hidden />
            {isHero ? heroLabel : label}
          </button>
        ))}
      </div>

      <form className="search-widget__form" onSubmit={handleSearch}>
        {renderToolbar()}
        <div className="sw-grid">
          {renderGrid()}
          <button type="submit" className="sw-submit" aria-label="Search">
            <Search size={22} strokeWidth={2.5} />
          </button>
        </div>
      </form>

      <DatePickerModal
        open={Boolean(dateModal)}
        title={dateModal?.title || 'Select date'}
        value={dateModal?.value}
        min={dateModal?.min}
        max={dateModal?.max}
        onClose={closeDate}
        onSelect={(d) => dateModal?.onSelect?.(d)}
      />

      {picker && (
        <TravellerPickerModal
          open
          title={picker.title}
          variant={picker.variant}
          values={picker.values}
          onClose={closePicker}
          onApply={picker.onApply}
        />
      )}
    </div>
  );
}
